import React, { useState } from "react";
import { useApp } from "@/contexts/AppContext";
import CustomerManager from "@/components/CustomerManager";
import RewardsManager from "@/components/RewardsManager";
import StaffDashboard from "@/components/StaffDashboard";
import AdminDebugPanel from "@/components/AdminDebugPanel";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { useIsMobile } from "@/hooks/use-mobile";
import { Database, Users, Gift, QrCode, Settings, LogOut, ChefHat, Crown, ClipboardList } from "lucide-react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { UserManagement } from "@/components/UserManagement";
import { LogsManager } from "@/components/LogsManager";

const Dashboard: React.FC = () => {
  const { logout, hasPermission, wipeAllData } = useApp();
  const [activeTab, setActiveTab] = useState("check-in");
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const isMobile = useIsMobile();

  // Determine accessible tabs based on permissions
  const canManageCustomers = hasPermission("manage_customers");
  const canManageRewards = hasPermission("manage_rewards");
  const isAdmin = hasPermission("manage_staff"); // Only admins can manage staff

  const handleWipeData = async () => {
    await wipeAllData();
    setResetDialogOpen(false);
  };

  return (
    <div className="flex flex-col min-h-screen bg-white">
      {/* Main Header */}
      <header className="bg-restaurant-secondary text-white shadow-md">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <h1 className="text-lg sm:text-xl md:text-2xl font-bold flex items-center">
              <img 
                src="/thai-express-logo.png" 
                alt="Thai Express" 
                className="h-8 mr-3 object-contain"
              />
              <span>Internal Dashboard</span>
            </h1>
            
            <Button 
              variant="ghost" 
              onClick={logout} 
              className="text-white hover:bg-white/10"
            >
              <LogOut className="mr-2 h-4 w-4" />
              <span className="hidden sm:inline">Logout</span>
            </Button>
          </div>
        </div>
      </header>

      {/* Single Tabs Component for Both Navigation and Content */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col">
        {/* Navigation Tabs */}
        <div className="bg-gray-100 border-b border-gray-200 shadow-sm overflow-x-auto">
          <div className="container mx-auto px-2 sm:px-4">
            <TabsList className="bg-transparent h-auto p-0 border-0 gap-1 w-full justify-start">
              <TabsTrigger 
                value="check-in" 
                className="px-2 sm:px-4 py-3 text-sm sm:text-base whitespace-nowrap rounded-none border-b-2 border-transparent data-[state=active]:border-restaurant-primary data-[state=active]:bg-transparent data-[state=active]:text-restaurant-primary font-medium"
              >
                <QrCode className="h-4 w-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">Customer Check-In</span>
              </TabsTrigger>
          
              {canManageCustomers && (
                <TabsTrigger 
                  value="customers" 
                  className="px-2 sm:px-4 py-3 text-sm sm:text-base whitespace-nowrap rounded-none border-b-2 border-transparent data-[state=active]:border-restaurant-primary data-[state=active]:bg-transparent data-[state=active]:text-restaurant-primary font-medium"
                >
                  <Users className="h-4 w-4 mr-1 sm:mr-2" />
                  Customers
                </TabsTrigger>
              )}
          
              {canManageRewards && (
                <TabsTrigger 
                  value="rewards" 
                  className="px-2 sm:px-4 py-3 text-sm sm:text-base whitespace-nowrap rounded-none border-b-2 border-transparent data-[state=active]:border-restaurant-primary data-[state=active]:bg-transparent data-[state=active]:text-restaurant-primary font-medium"
                >
                  <Gift className="h-4 w-4 mr-1 sm:mr-2" />
                  Rewards
                </TabsTrigger>
              )}
          
              {canManageRewards && (
                <TabsTrigger 
                  value="vip" 
                  className="px-2 sm:px-4 py-3 text-sm sm:text-base whitespace-nowrap rounded-none border-b-2 border-transparent data-[state=active]:border-restaurant-primary data-[state=active]:bg-transparent data-[state=active]:text-restaurant-primary font-medium"
                >
                  <Crown className="h-4 w-4 mr-1 sm:mr-2" />
                  VIP Program
                </TabsTrigger>
              )}
          
              {isAdmin && (
                <TabsTrigger 
                  value="admin" 
                  className="px-2 sm:px-4 py-3 text-sm sm:text-base whitespace-nowrap rounded-none border-b-2 border-transparent data-[state=active]:border-restaurant-primary data-[state=active]:bg-transparent data-[state=active]:text-restaurant-primary font-medium"
                >
                  <Settings className="h-4 w-4 mr-1 sm:mr-2" />
                  Admin
                </TabsTrigger>
              )}
            </TabsList>
          </div>
        </div>
        
        {/* Main Content Area */}
        <div className="flex-1 overflow-x-hidden">
          <div className="container mx-auto px-2 sm:px-4 py-4 sm:py-6">
            {/* Check-In Tab - Staff Interface */}
            <TabsContent value="check-in" className="mt-0 data-[state=active]:animate-in">
              <StaffDashboard />
            </TabsContent>

            {/* Customers Tab */}
            {canManageCustomers && (
              <TabsContent value="customers" className="mt-0 data-[state=active]:animate-in">
                <CustomerManager />
              </TabsContent>
            )}

            {/* Rewards Tab */}
            {canManageRewards && (
              <TabsContent value="rewards" className="mt-0 data-[state=active]:animate-in">
                <RewardsManager />
              </TabsContent>
            )}
            
            {/* VIP Program Tab */}
            {canManageRewards && (
              <TabsContent value="vip" className="mt-0 data-[state=active]:animate-in">
                <Card className="border-t-4 border-t-restaurant-primary shadow-lg">
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center text-lg sm:text-xl">
                      <Crown className="w-5 h-5 mr-2 text-restaurant-primary" />
                      VIP Program Management
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-4 sm:p-6">
                    <p className="text-muted-foreground mb-4">
                      VIP Program features coming soon. This section will allow you to manage special offers and exclusive benefits for your VIP customers.
                    </p>
                  </CardContent>
                </Card>
              </TabsContent>
            )}
        
            {/* Admin Panel Tab */}
            {isAdmin && (
              <TabsContent value="admin" className="space-y-4 sm:space-y-6 mt-0 data-[state=active]:animate-in">
                {/* System Logs Section */}
                <Card className="border-t-4 border-t-restaurant-primary shadow-lg">
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center text-base sm:text-lg">
                      <ClipboardList className="h-5 w-5 mr-2 text-restaurant-primary" />
                      System Logs
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-3 sm:p-6">
                    <LogsManager />
                  </CardContent>
                </Card>
                
                {/* Staff Management Section */}
                <Card className="border-t-4 border-t-restaurant-primary shadow-lg">
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center text-base sm:text-lg">
                      <Users className="h-5 w-5 mr-2 text-restaurant-primary" />
                      Staff Management
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-3 sm:p-6">
                    <UserManagement />
                  </CardContent>
                </Card>

                {/* Admin Debug Panel for Rewards Testing */}
                <AdminDebugPanel />

                {/* Danger Zone Section */}
                <Card className="border-t-4 border-t-destructive shadow-lg">
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center text-base sm:text-lg">
                      <Database className="h-5 w-5 mr-2 text-destructive" />
                      Danger Zone
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-3 sm:p-6">
                    <AlertDialog open={resetDialogOpen} onOpenChange={setResetDialogOpen}>
                      <AlertDialogTrigger asChild>
                        <Button variant="destructive" className="w-full sm:w-auto">
                          Reset All Customer Data
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent className="max-w-[95vw] sm:max-w-lg">
                        <AlertDialogHeader>
                          <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This will permanently delete all customer data including visit history and 
                            redeemed rewards. This action cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter className="flex-col sm:flex-row gap-2">
                          <AlertDialogCancel className="w-full sm:w-auto mt-0">Cancel</AlertDialogCancel>
                          <AlertDialogAction 
                            onClick={(e) => {
                              e.preventDefault();
                              handleWipeData();
                            }} 
                            className="w-full sm:w-auto bg-restaurant-primary hover:bg-restaurant-primary-hover">
                            Yes, delete all data
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </CardContent>
                </Card>
              </TabsContent>
            )}
          </div>
        </div>
      </Tabs>
    </div>
  );
};

export default Dashboard;
